import * as React from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import {
	useApi,
	type APIParams,
	type APIRequest,
} from '@hooks/use-api-requests.hook'
import { useAppContext } from '@hooks/use-app-context'
import { Actions } from '@store/reducer'

import { useAtom, useSetAtom } from 'jotai'
import {
	currentStepAtom,
	documentFlowVariantAtom,
	qrDisclaimerAtom,
	selectedSubStepAtom,
	selectedValueAtom,
	subStepExistsAtom,
} from '@store/atoms'
import { StepsRichiesta } from '@store/stepsRichiesta'
import { DocumentMode } from '@ui/richiesta/stepper/data/steps'

import { useBrowserStatePolling } from './pages/mobile-upload/useBrowserStatePolling'
import { isDeviceMobile } from '@utils/mobile'
import { MOBILE_QR_ACTIVE_KEY, MOBILE_QR_START_URL_KEY } from '@store/store'

type PollingState = 'PENDING' | 'ACTIVE' | 'CLOSED' | 'EXPIRED' | 'REVIEW'

type BrowserSession = {
	idRichiesta: number
	source: 'MOBILE' | 'DESKTOP' | null
	status: PollingState | null
}

export const MobileBootFromQR: React.FC = () => {
	const location = useLocation()

	const docIdFromUrl = React.useMemo(() => {
		const params = new URLSearchParams(location.search)
		const value = params.get('docId')?.trim()

		return value ? value : null
	}, [location.search])

	const sessioneFromUrl = React.useMemo(() => {
		const params = new URLSearchParams(location.search)
		const value = params.get('sessione')?.trim()

		return value ? value : null
	}, [location.search])

	const isMobile = isDeviceMobile()
	const navigate = useNavigate()
	const { token } = useParams<{ token?: string }>()
	const { state, dispatch } = useAppContext()

	// atoms (desktop design standard)
	const setCurrentStep = useSetAtom(currentStepAtom)
	const setSelectedSubStep = useSetAtom(selectedSubStepAtom)
	const setSubStepExists = useSetAtom(subStepExistsAtom)
	const setSelectedValueMap = useSetAtom(selectedValueAtom)
	const setDocumentFlowVariant = useSetAtom(documentFlowVariantAtom)
	const [qrDisclaimer, setQrDislcaimer] = useAtom(qrDisclaimerAtom) //user for set disclaimer in qr session no repalced

	const { session, start, stop } = useBrowserStatePolling(token ?? '')

	// stabilizza sessione per evitare flicker
	const stableSessionRef = React.useRef<BrowserSession | null>(null)
	React.useEffect(() => {
		if (session?.idRichiesta && session.idRichiesta > 0) {
			stableSessionRef.current = session
		}
	}, [session])

	const stableSession = stableSessionRef.current
	const idRichiesta = stableSession?.idRichiesta ?? 0
	const status = stableSession?.status ?? null

	///dispatch sessione necessaria per recuperare i docuneti salvati su missingdocument QR FLOW
	React.useEffect(() => {
		if (!sessioneFromUrl) return

		dispatch({
			type: Actions.UpdateData,
			payload: {
				sessione: sessioneFromUrl,
			},
		})
	}, [sessioneFromUrl, dispatch])

	// validateMobileSession
	const validateConfig = React.useMemo<APIParams<boolean>>(
		() => ({
			request: 'DbOperations/validateMobileSession' as APIRequest,
			method: 'POST',
			shouldCache: false,
			sleeper: true,
			silent: true,
		}),
		[]
	)
	const [valid, validateSession] = useApi<boolean>(validateConfig)

	const [bootError, setBootError] = React.useState<string | null>(null)

	/////---------SALVO LA URL DEL QRCODE
	React.useEffect(() => {
		if (!token) return

		sessionStorage.setItem(MOBILE_QR_START_URL_KEY, window.location.href)
		sessionStorage.setItem(MOBILE_QR_ACTIVE_KEY, '1')
	}, [token])

	// 1) salva token in store
	React.useEffect(() => {
		if (!token) return
		dispatch({ type: Actions.UpdateMobileToken, payload: token })
	}, [token, dispatch])

	// 2) start polling una sola volta
	React.useEffect(() => {
		if (!token) return
		start()
		return () => stop()
	}, [token, start, stop])

	// 3) validate una sola volta quando arriva idRichiesta
	const validatedRef = React.useRef(false)
	React.useEffect(() => {
		if (!token) return
		if (!idRichiesta) return
		if (validatedRef.current) return
		validatedRef.current = true

		validateSession({ body: { idSessioneMobile: token, idRichiesta } } as any)
	}, [token, idRichiesta, validateSession])

	// 4) se scade, stop + messaggio
	React.useEffect(() => {
		if (status !== 'EXPIRED') return
		stop()
		setBootError('Sessione scaduta. Riprova dal desktop.')
	}, [status, stop])

	// 5) quando valid === true → set atom per entrare nel wizard standard + navigate

	const navigatedRef = React.useRef(false)
	React.useEffect(() => {
		if (!token) return
		if (!idRichiesta) return
		if (valid !== true) return
		if (navigatedRef.current) return
		navigatedRef.current = true

		// salva idRichiesta nel global state (serve poi per SaveDocuments/OCR ecc.)
		dispatch({ type: Actions.SetIdRichiesta, payload: idRichiesta })

		setQrDislcaimer(true) // sono certo che siamo nella procedura mobile della richiesta

		if (docIdFromUrl) {
			navigate(
				`/Home/MobileMissingDocumentsUpload/${token}?docId=${docIdFromUrl}`,
				{ replace: true }
			)
			return
		}

		// entra nello STEP CONTATTI + substep document wizard
		setCurrentStep(StepsRichiesta.CONTATTI)

		const defaultDocumentMode = isDeviceMobile()
			? DocumentMode.ALLEGA
			: DocumentMode.FOTOGRAFA

		setSelectedValueMap((prev) => ({
			...prev,
			[StepsRichiesta.CONTATTI]: defaultDocumentMode,
		}))

		/* 	setSelectedValueMap((prev) => ({
			...prev,
			[StepsRichiesta.CONTATTI]: DocumentMode.FOTOGRAFA, // o ALLEGA se vuoi partire diretto
		}))
		 */

		setSelectedSubStep('selectDocument') // wizard standard
		setSubStepExists(true)

		setDocumentFlowVariant('mobileQr') ///nasconde il back button

		// IMPORTANTISSIMO:
		// NON stop qui se sul desktop c'è ancora poll attivo e serve;
		// sul mobile boot puoi fermarlo perché hai già navigato nello step standard.
		stop()

		navigate('/richiesta', { replace: true })
	}, [
		token,
		idRichiesta,
		valid,
		dispatch,
		navigate,
		stop,
		setCurrentStep,
		setSelectedValueMap,
		setSelectedSubStep,
		setSubStepExists,
		docIdFromUrl,
	])

	// render boot
	if (!token) {
		return <div className='p-4'>Token mancante</div>
	}

	if (bootError) {
		return (
			<div className='p-4'>
				<div className='text-(--primary-blue) text-[22px] font-semibold'>
					Errore
				</div>
				<div className='mt-2'>{bootError}</div>
			</div>
		)
	}

	return (
		<div className='p-4'>
			{/* <div className='text-(--primary-blue) text-[22px] font-semibold'>
				Sto aprendo la richiesta…
			</div>

			<div className='mt-2 text-sm opacity-70'>
				token: {token}
				<br />
				idRichiesta: {idRichiesta || '…'}
				<br />
				status: {String(status ?? '…')}
				<br />
				valid: {String(valid)}
			</div> */}

			{valid === false && (
				<div className='mt-3 text-red-600'>
					Sessione non valida. Riprova dal desktop.
				</div>
			)}
		</div>
	)
}
